import moment from 'moment';

import { TASK_PRIORITIES, TASK_STATUS_LIST, TaskPriority, TaskStatus } from '@qwealth/qdata';

import { DateRange } from './DateRange';
import { ACTIVITY_TYPE_LOGS, ACTIVITY_TYPE_TASKS, ActivityType, ActivityTypes } from './Activity';
import { SortDirection } from './SortDirection';
import { ALL_DATE_RANGE, ASSIGN_TO_ME_RESPONSIBILITY, CREATED_BY_ME_RESPONSIBILITY } from '../refData/acitivites';
import { getCallLogSavedFilters, getTaskSavedFilters } from 'utils/activityFilterHelper';
import { omit } from 'lodash';
import { AssignedWorkflowsFilters } from './Workflows';

export const ACTIVITY_FILTER_KEY = 'activity_filters_v8';
export const CALL_LOG_FILTERS_KEY = 'call_log_filters';
export const TASK_FILTERS_KEY = 'task_filters';
export const UNTAGGED_TASKS = 'Untagged';
export const ALL_LOG_SUBJECTS = 'All Subjects';

export type ActivityFilters = {
  filterQuery: string;
  filterDateValue: string;
  filterActivities: Array<ActivityType>;
  filterTaskResponsibilities: Array<string>;
  filterTaskStatus: Array<TaskStatus>;
  filterTaskTags: Array<string>;
  filterTaskPriorities: Array<TaskPriority>;
  filterLogSubject: string;
  filterDateRange: DateRange;
  sortDirection: SortDirection;
};

export type ActivityFiltersWithoutQuery = Omit<ActivityFilters, 'filterQuery'>;

export type HouseholdActivityFilters = ActivityFiltersWithoutQuery & {
  householdQID: string;
  timeZone: string;
};

export const DATE_FILTERS = [
  { label: 'All Time', value: ALL_DATE_RANGE },
  { label: 'This Week', value: 'this_week' },
  { label: 'All Upcoming', value: 'all_upcoming' },
  { label: 'All Past', value: 'all_past' },
  { label: 'Custom', value: 'custom' },
];

export const getDateFilterByValue = (value: string) => {
  return DATE_FILTERS.find(filter => filter.value === value) || DATE_FILTERS[0];
};

export const defaultFilter: ActivityFilters = {
  sortDirection: 'DESC',
  filterQuery: '',
  filterDateValue: DATE_FILTERS[0].value,
  filterActivities: [...ActivityTypes],
  filterTaskResponsibilities: [ASSIGN_TO_ME_RESPONSIBILITY.value],
  filterTaskStatus: [...TASK_STATUS_LIST],
  filterTaskTags: [],
  filterTaskPriorities: [...TASK_PRIORITIES],
  filterLogSubject: ALL_LOG_SUBJECTS,
  filterDateRange: {
    fromDate: moment().startOf('day'),
    toDate: moment().add(7, 'day').endOf('day'),
  },
};

export interface LogFilterParam {
  filterDateRange: DateRange;
  filterDateValue: string;
  filterLogSubject: string;
  householdQID?: string;
  sortDirection: SortDirection;
  filterQuery?: string;
}

export const defaultCallLogFilters: LogFilterParam = {
  filterDateRange: {
    fromDate: moment().startOf('day'),
    toDate: moment().add(7, 'day').endOf('day'),
  },
  filterDateValue: DATE_FILTERS[0].value,
  filterLogSubject: ALL_LOG_SUBJECTS,
  sortDirection: 'DESC',
};

export const updateSortDirectionInLocalStorage = (ActivityType: ActivityType, sortDirection: 'DESC' | 'ASC') => {
  switch (ActivityType) {
    case ACTIVITY_TYPE_LOGS: {
      const updatedCallLogFilters: LogFilterParam = { ...getCallLogSavedFilters(), sortDirection };
      localStorage.setItem(CALL_LOG_FILTERS_KEY, JSON.stringify(updatedCallLogFilters));
      return;
    }
    case ACTIVITY_TYPE_TASKS: {
      const updatedTaskFilters: TaskFilterParam = { ...omit(getTaskSavedFilters(), 'filterQuery'), sortDirection };
      localStorage.setItem(TASK_FILTERS_KEY, JSON.stringify(updatedTaskFilters));
      return;
    }
    default:
      return;
  }
};

export type TaskFilterParam = {
  filterDateRange: DateRange;
  filterDateValue: string;
  filterTaskResponsibilities: Array<string>;
  filterTaskStatus: Array<TaskStatus>;
  filterTaskTags: Array<string>;
  filterTaskPriorities: Array<TaskPriority>;
  sortDirection: SortDirection;
  householdQID?: string;
  filterQuery?: string;
};

export type QFeedTaskFilterParam = TaskFilterParam & {
  userEmail: string;
};

export const defaultTaskFilters: TaskFilterParam = {
  filterDateRange: {
    fromDate: moment().startOf('day'),
    toDate: moment().add(7, 'day').endOf('day'),
  },
  filterDateValue: DATE_FILTERS[0].value,
  sortDirection: 'DESC',
  filterTaskResponsibilities: [ASSIGN_TO_ME_RESPONSIBILITY.value, CREATED_BY_ME_RESPONSIBILITY.value],
  filterTaskStatus: [],
  filterTaskTags: [],
  filterTaskPriorities: [],
  filterQuery: '',
};

export type ActivityFilterTypes = LogFilterParam | TaskFilterParam | AssignedWorkflowsFilters;

//TODO: This type will be gone when household activities are migrated to use the same filters as the qfeed activities. At that point, ActivityFilters will be removed and just use ActivityFilterTypes everywhere.
export type AllActivityFilters = ActivityFilters | ActivityFilterTypes;
